import { initializeApp } from "firebase/app";
import { deleteToken as _deleteToken, getMessaging, getToken, type Messaging, onMessage } from "firebase/messaging";
import { firebaseConfig, vapidKey } from "~/firebase.config";
import { useDYAppConfig } from "~/modules/nuxt3-module-doyac/composables/config";
import { $dy__fetchWithToken } from "~/modules/nuxt3-module-doyac/composables/fetch";

/**
 * FCM 웹 푸시를 수신하기 위한 Composable. 여기서 구현되어있는 건 포그라운드 상태일 때 메시지 수신이며 백그라운드일 때 메시지 수신은
 * 각 도메인 루트의 firebase-messaging-sw.js 파일에서 구현된다. firebase-messaging-sw.js 파일은 단순히 백그라운드 메시지만 수신하는게
 * 아니고 웹 푸시 수신을 위한 Service Worker 등록을 하기 때문에 반드시 준비되어 있어야 한다.
 */
export function useDYAFirebaseMessaging() {
  const { API_BASE_URL } = useDYAppConfig()

  let messaging: Messaging | null
  let retryCount = 0

  const getTokenAndSendToServer = () => {
    if (!messaging) {
      console.error('init 메서드 호출이 필요합니다.')
      return
    }

    getToken(messaging, { vapidKey }).then((currentToken) => {
      if (currentToken) {
        // console.log('currentToken', currentToken)
        registerToken(currentToken).then()
      } else {
        // Show permission request UI
        console.log('No registration token available. Request permission to generate one.');
        // requestPermission()
      }
    }).catch((err: any) => {
      console.log('An error occurred while retrieving token. ', err);
      // 최초 요청일 때에는 아직 서비스 워커로서 firebase-messaging-sw.js 파일을 등록하지 못한 상태일 수 있어서 실패하므로 몇 차례
      // 반복시켜야 할 수 있음
      if (retryCount < 5) {
        getTokenAndSendToServer()
        retryCount++
      }
    });
  }

  const init = () => {
    try {
      const app = initializeApp(firebaseConfig)

      messaging = getMessaging(app)
      // console.log('messaging', messaging)

      getTokenAndSendToServer()

      onMessage(messaging, async (payload) => {
        console.log('Message received. ', payload)

        const show = async () => {
          const registrations = await navigator.serviceWorker.getRegistrations()
          await registrations.find(
            i => i.scope.includes('/firebase-cloud-messaging-push-scope')
          )?.showNotification(payload.data?.title ? `${payload.data?.title}` : 'Title', {
            ...payload.data,
            data: {
              link: payload.data?.link
            }
          })
        }

        // 2026-04-30: 한 사용자가 어드민 탭 여러 개를 (특히 멀티 모니터에서 모두 visible 상태로) 동시에 띄워둔 경우,
        // 각 탭의 onMessage 가 동시에 fire 되면서 같은 알림이 N번 표시되던 문제 방지.
        // navigator.locks 로 한 탭만 처리하도록 leader election. 미지원 브라우저는 visibility 단탭 fallback.
        if (typeof navigator === 'undefined' || !('locks' in navigator)) {
          if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            await show()
          }
          return
        }

        const lockKey = `fcm-msg-${(payload as any).messageId ?? JSON.stringify(payload.data ?? {})}`
        await navigator.locks.request(lockKey, { ifAvailable: true }, async (lock) => {
          if (!lock) return  // 다른 탭이 먼저 잡음 → 이 탭은 skip
          await show()
          await new Promise(r => setTimeout(r, 500))  // 같은 메시지 받은 다른 탭이 양보하도록 락 유지
        })
      });
    } catch {
      // Service Worker를 지원하지 않는 환경(headless 브라우저 등)에서는 무시
    }
  }

  const deleteToken = () => {
    if (!messaging) {
      console.error('init 메서드 호출이 필요합니다.')
      return
    }

    _deleteToken(messaging).then()
  }

  const registerToken = async (token: string) => {
    return await $dy__fetchWithToken<string>(
      `${API_BASE_URL}/api/fcm-web-tokens/token/${token}`,
      {
        method: 'post', body: { token }
      }
    )
  }

  return {
    init, deleteToken
  }
}